/**
 * 출퇴근 위치 확인의 판정 (DOM 없음 — tests/unit/attendance-geo-logic.test.mjs).
 *
 * 화면(attendance-geo.js)은 위치가 필요할 때만 잡는다: 회사 IP 로 이미 통과하거나 승인된 재택 · 외근 · 출장일이면
 * 서버가 data-need 를 내리지 않는다. 위치는 「미리 확인」(POST /attendance/locate — 저장 없음)으로 결과를 보여 주고,
 * 출퇴근 단추를 누르면 새로 잡은 위치를 실어 보낸다 — 서버가 거리를 다시 재므로 화면의 판정은 안내일 뿐이다.
 *
 * locHelpCase — 거부된 위치 권한을 켜는 곳(브라우저는 거부된 사이트에 다시 묻지 않는다):
 *   android-app     — 안드로이드 설치 앱: 위치는 앱 설정이 아니라 크롬의 사이트 설정이 쥔다(알림과 다르다)
 *   android-browser — 폰 브라우저 탭: 주소창 왼쪽 사이트 정보 → 권한 → 위치
 *   ios             — 아이폰 · 아이패드(사파리 탭과 홈 화면 앱 모두): 설정 → 개인정보 보호 및 보안 → 위치 서비스
 *   desktop-app     — 데스크톱 설치 앱 창: ⋮ → 앱 정보 → 위치
 *   desktop-browser — 데스크톱 브라우저 탭: 사이트 정보 → 위치 (+ 운영체제의 위치 설정)
 *
 * locState — 띠와 시트가 함께 쓰는 상태:
 *   idle · checking · ok · need(아직 묻지 않음) · blocked(거부) · unavailable(신호 없음 · GPS 꺼짐) · timeout ·
 *   outside(오차를 감안해도 반경 밖) · inaccurate(오차가 반경보다 큼) · approximate(오차 1km 이상 — 「대략적인 위치」만
 *   허용된 기기) · unsupported(브라우저에 위치 없음) · insecure(https 아님) · ip_only(위치로 받는 근무지가 없음) ·
 *   failed(서버에 묻지 못함)
 *
 * 「정확한 위치」는 사이트가 강제할 수 없다 — 크롬 안드로이드(2026-05~)는 위치를 물을 때 사이트마다 정확/대략을 고르게
 * 하고, 안드로이드 · iOS 의 앱 권한에도 같은 선택이 있다. 대략적인 위치는 오차가 약 1.5km 라 서버가 거절하고(오차 >
 * 반경), 화면은 그 경우를 따로 알아보고 정확한 위치를 켜는 곳을 보인다.
 */
import { pushHelpCase } from './push-permission-logic.js';

// COARSE_ACCURACY is the error (m) from which a fix reads as an approximate location: Chrome's and Android's
// approximate grant is about ±1.5 km and iOS's several km, while GPS and Wi-Fi fixes stay well under 1 km.
export const COARSE_ACCURACY = 1000;

export function locHelpCase(env = {}) {
    const k = pushHelpCase(env);
    if (k === 'ios-app' || k === 'ios-browser') return 'ios';
    return k;
}

// betterFix keeps the more precise of two positions (the newer one on a tie).
export function betterFix(best, next) {
    if (!next) return best || null;
    if (!best) return next;
    if (next.accuracy < best.accuracy) return next;
    if (next.accuracy === best.accuracy && next.at >= best.at) return next;
    return best;
}

// How a look runs (attendance-geo.js locateRun): each notably sharper fix is judged by the server as it
// comes, and the look ends at the first verdict more waiting cannot change. Only a rough fix (inaccurate)
// waits a little for a sharper one before it shows — phones often follow a first network fix with a GPS
// fix within a second or two — and keeps being sharpened quietly after it shows, for the rest of the window.
// SETTLE_MS is that little wait; an approximate fix shows at once (it does not sharpen while 「대략적인
// 위치」 is what was allowed).
export const SETTLE_MS = 1500;
// UNAVAILABLE_GRACE_MS: after a "position unavailable" error with no fix in hand, how long a late fix may
// still come before the look gives up (the error usually means location is off on the device).
export const UNAVAILABLE_GRACE_MS = 1500;
// SLOW_HINT_MS: a look still waiting after this long has had no fix at all — the strip says the signal is slow.
export const SLOW_HINT_MS = 4000;
// VERDICT_FRESH_MS: how long a failed look stays good enough to open its explanation at once on a clock press
// (a fresh look then runs behind the sheet).
export const VERDICT_FRESH_MS = 60000;

// verdictFinal reports whether more waiting cannot change a verdict: everything but a rough fix.
export function verdictFinal(state) {
    return state !== 'inaccurate' && state !== 'approximate';
}

// worthJudging reports whether a fix is sharp enough beyond the one last judged to ask the server again
// (a quarter smaller error, so a GPS fix settling metre by metre is not judged over and over).
export function worthJudging(judged, next) {
    if (!next) return false;
    if (!judged) return true;
    return next.accuracy <= judged.accuracy * 0.75;
}

// settleDelay is how much longer (ms) a verdict waits before it shows, elapsedMs into the look: a final or an
// approximate one at once, an inaccurate one until SETTLE_MS.
export function settleDelay(state, elapsedMs) {
    if (state !== 'inaccurate') return 0;
    return Math.max(0, SETTLE_MS - (Number(elapsedMs) || 0));
}

// pressOpensSheet reports whether a clock press opens the last look's explanation at once: the look failed
// on the position (not on a permission or the server) no longer than VERDICT_FRESH_MS ago.
export function pressOpensSheet(state, verdictAt, now) {
    if (!['outside', 'inaccurate', 'approximate', 'unavailable', 'timeout'].includes(state)) return false;
    return verdictAt > 0 && now - verdictAt <= VERDICT_FRESH_MS;
}

// fresh reports whether a fix is recent enough to send with a clock (default 60 s).
export function fresh(fix, now, maxAgeMs = 60000) {
    return !!fix && now - fix.at <= maxAgeMs;
}

// errorState maps a GeolocationPositionError code to a state.
export function errorState(code) {
    if (code === 1) return 'blocked';
    if (code === 3) return 'timeout';
    return 'unavailable';
}

// verdictState maps a /attendance/locate answer to a state.
export function verdictState(resp) {
    if (!resp || typeof resp !== 'object') return 'failed';
    if (resp.ok) return 'ok';
    switch (resp.reason) {
        case 'outside': return 'outside';
        case 'inaccurate': return Number(resp.accuracy) >= COARSE_ACCURACY ? 'approximate' : 'inaccurate';
        case 'need_location': return 'need';
        case 'ip_only': return 'ip_only';
        case 'no_workplaces': return 'ip_only';
    }
    return 'failed';
}

// sheetModeFor is the sheet page each state opens (null: nothing to explain).
export function sheetModeFor(state) {
    switch (state) {
        case 'need': return 'ask';
        case 'blocked': return 'blocked';
        case 'unavailable':
        case 'timeout': return 'unavailable';
        case 'outside': return 'outside';
        case 'inaccurate': return 'inaccurate';
        case 'approximate': return 'approximate';
        case 'unsupported':
        case 'insecure': return 'unsupported';
    }
    return null;
}

// clockAllowed reports whether a clock button may submit right away in this state: only after the
// check passed. Everything else first opens the sheet (or runs the check).
export function clockAllowed(state) {
    return state === 'ok';
}

// formatDistance writes metres as "120m" or "1.2km" through the page's two labels.
export function formatDistance(m, labels = {}) {
    const n = Math.max(0, Math.round(Number(m) || 0));
    if (n < 1000) return fill(labels.m || '{n}m', { n });
    const km = n >= 10000 ? Math.round(n / 1000) : Math.round(n / 100) / 10;
    return fill(labels.km || '{n}km', { n: km });
}

// fill replaces {key} tokens; unknown tokens stay as written.
export function fill(text, vars) {
    return String(text == null ? '' : text).replace(/\{(\w+)\}/g, (m, k) => (vars && Object.prototype.hasOwnProperty.call(vars, k) ? String(vars[k]) : m));
}

// stripText is the one-line status for a state (labels are the page's strip_* strings).
export function stripText(state, info = {}, labels = {}) {
    const dist = info.distance != null ? formatDistance(info.distance, labels.dist) : '';
    const name = info.name || '';
    switch (state) {
        case 'checking': return labels.checking || '';
        case 'ok':
            if (info.method === 'request') return fill(labels.request, { name: info.request || '' });
            if (info.method === 'ip') return fill(labels.okIP, { name });
            return fill(labels.ok, { name, dist });
        case 'need': return labels.need || '';
        case 'blocked': return labels.blocked || '';
        case 'unavailable':
        case 'timeout': return labels.unavailable || '';
        case 'outside': return name ? fill(labels.outside, { name, dist }) : (labels.unavailable || '');
        case 'inaccurate': return fill(labels.inaccurate, { acc: formatDistance(info.accuracy, labels.dist) });
        case 'approximate': return fill(labels.approximate, { acc: formatDistance(info.accuracy, labels.dist) });
        case 'unsupported':
        case 'insecure': return labels.unsupported || '';
        case 'ip_only': return labels.ipOnly || '';
        case 'failed': return labels.failed || '';
    }
    return '';
}

// stripAction is the strip's one button for a state: 'check' (ask / first look), 'recheck', 'howto'
// (blocked → how to turn it on), 'help' (explain why), or null.
export function stripAction(state) {
    switch (state) {
        case 'need': return 'check';
        case 'blocked': return 'howto';
        case 'unavailable':
        case 'timeout':
        case 'failed': return 'recheck';
        case 'outside':
        case 'inaccurate':
        case 'approximate':
        case 'unsupported':
        case 'insecure': return 'help';
        case 'ok': return 'recheck';
    }
    return null;
}
